/**
 * Drop Code — the rules, with no DOM in sight.
 *
 * A trooper stands in the field waiting on support. Each call-in has a fixed directional
 * code; punch it in on the four-way pad before the window closes and it lands. The codes
 * never change between rounds, so a returning player stops reading them and starts knowing
 * them — which is the same skill Taco Slap asks for with its hidden chant.
 *
 *   right direction        → the cursor advances; the last one lands the call-in.
 *   wrong direction        → penalty, and the code resets to the start. You keep the call-in.
 *   window closes          → penalty, the call-in is lost, the next one comes up.
 *
 * A mistake here costs time but not the call-in, which is deliberately unlike the app's other
 * two games (Taco Slap puts the pile back, Lu or Labu ends the round). Three games, three
 * different things a mistake means.
 *
 * Everything is driven by a `now` (ms) the caller passes in, so it is testable and pause-able.
 *
 * `seeded` stays per game folder so the rules are self-contained; `formatTime` is shared from
 * `src/ui/format.ts`. See luorlabu/engine.ts.
 */

export type Dir = 'up' | 'down' | 'left' | 'right';
export const DIRS: readonly Dir[] = ['up', 'down', 'left', 'right'] as const;
export const ARROW: Record<Dir, string> = { up: '↑', down: '↓', left: '←', right: '→' };

export interface Call {
  id: string;
  name: string;
  icon: string;
  code: Dir[];
}

/**
 * Our own call-ins, not the ones from the game this borrows its shape from — the same move
 * Taco Slap made when it renamed the specials. Codes are fixed so they can be learned.
 */
export const CALLS: readonly Call[] = [
  { id: 'flare', name: 'Flare', icon: '🔦', code: ['up', 'down', 'right'] },
  { id: 'marker', name: 'Marker', icon: '🎯', code: ['right', 'right', 'up'] },
  { id: 'resupply', name: 'Resupply', icon: '📦', code: ['down', 'down', 'up', 'right'] },
  { id: 'shield', name: 'Shield Dome', icon: '🛡️', code: ['down', 'up', 'left', 'right', 'up'] },
  { id: 'surge', name: 'Power Surge', icon: '⚡', code: ['left', 'down', 'right', 'down', 'up'] },
  { id: 'freeze', name: 'Freeze Field', icon: '🧊', code: ['down', 'left', 'up', 'down', 'right', 'left'] },
  { id: 'satellite', name: 'Satellite', icon: '🛰️', code: ['up', 'right', 'down', 'left', 'up', 'right'] },
  { id: 'evac', name: 'Extraction', icon: '🚁', code: ['up', 'up', 'left', 'down', 'right', 'down', 'up'] },
];

export interface Rules {
  /** call-ins to land before the round is over */
  calls: number;
  /** ms allowed for a call-in, before its per-step allowance */
  baseMs: number;
  /** extra ms allowed per direction in the code */
  perStepMs: number;
  /** ms added to the clock for a wrong direction or a lost call-in */
  penaltyMs: number;
}

export const DEFAULT_RULES: Rules = {
  calls: 10,
  baseMs: 1200,
  perStepMs: 520,
  penaltyMs: 2000,
};

export type Status = 'idle' | 'playing' | 'won';

export interface Outcome {
  ok: boolean;
  /** what was pressed; 'timeout' when the window closed */
  did: Dir | 'timeout';
  expected: Dir;
  call: Call;
  /** the call-in was completed */
  landed: boolean;
  /** the window closed before it was completed */
  lost: boolean;
  won: boolean;
}

export interface Snapshot {
  status: Status;
  call: Call | null;
  /** directions entered correctly so far on this call-in */
  cursor: number;
  index: number;
  calls: number;
  landed: number;
  mistakes: number;
  streak: number;
}

function shuffle<T>(xs: T[], rng: () => number): T[] {
  const a = xs.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * A queue that ramps: short codes first, long ones last, with enough jitter that it is not
 * the same order every time. Never the same call-in twice in a row.
 */
export function buildQueue(rules: Rules, rng: () => number = Math.random): Call[] {
  const sorted = [...CALLS].sort((a, b) => a.code.length - b.code.length);
  const out: Call[] = [];
  for (let i = 0; i < rules.calls; i++) {
    const t = rules.calls === 1 ? 0 : i / (rules.calls - 1);
    const centre = t * (sorted.length - 1);
    // a window of three around the ramp point, minus whatever we just used
    const near = [-1, 0, 1]
      .map((d) => sorted[Math.max(0, Math.min(sorted.length - 1, Math.round(centre + d)))])
      .filter((c, idx, arr) => arr.indexOf(c) === idx);
    const fresh = near.filter((c) => c.id !== out[out.length - 1]?.id);
    out.push(shuffle(fresh.length ? fresh : near, rng)[0]);
  }
  return out;
}

export function windowFor(call: Call, rules: Rules): number {
  return rules.baseMs + rules.perStepMs * call.code.length;
}

export class DropCode {
  readonly rules: Rules;
  private queue: Call[] = [];
  private index = 0;
  private cursor = 0;
  private startAt = 0;
  private callAt = 0;
  private penalties = 0;
  private pausedTotal = 0;
  private pausedAt: number | null = null;
  private status: Status = 'idle';
  private prepared = false;
  private rng: () => number;
  landed = 0;
  mistakes = 0;
  streak = 0;
  bestStreak = 0;

  constructor(rules: Partial<Rules> = {}, rng: () => number = Math.random) {
    this.rules = { ...DEFAULT_RULES, ...rules };
    this.rng = rng;
  }

  get call(): Call | null {
    return this.queue[this.index] ?? null;
  }

  /** directions entered correctly so far on the current call-in */
  get progress(): number {
    return this.cursor;
  }

  /** how far through the queue we are */
  get callIndex(): number {
    return this.index;
  }

  snapshot(): Snapshot {
    return {
      status: this.status,
      call: this.call,
      cursor: this.cursor,
      index: this.index,
      calls: this.rules.calls,
      landed: this.landed,
      mistakes: this.mistakes,
      streak: this.streak,
    };
  }

  /** the direction the player should press right now */
  get expected(): Dir | null {
    return this.call ? this.call.code[this.cursor] : null;
  }

  /** build the queue without starting the clock, so the view can settle first */
  prepare(): Call[] {
    this.queue = buildQueue(this.rules, this.rng);
    this.prepared = true;
    return this.queue;
  }

  start(now: number): void {
    if (!this.prepared) this.prepare();
    this.prepared = false;
    this.index = 0;
    this.cursor = 0;
    this.landed = 0;
    this.mistakes = 0;
    this.streak = 0;
    this.bestStreak = 0;
    this.penalties = 0;
    this.pausedTotal = 0;
    this.pausedAt = null;
    this.startAt = now;
    this.callAt = now;
    this.status = 'playing';
  }

  /** the running clock: wall time minus pauses plus penalties */
  elapsed(now: number): number {
    if (this.status === 'idle') return 0;
    const end = this.pausedAt ?? now;
    return Math.max(0, end - this.startAt - this.pausedTotal + this.penalties);
  }

  /** ms allowed for the current call-in */
  get windowMs(): number {
    return this.call ? windowFor(this.call, this.rules) : this.rules.baseMs;
  }

  /** 1 → the call-in just came up, 0 → out of time */
  remaining(now: number): number {
    if (this.status !== 'playing' || this.pausedAt !== null) return 1;
    return Math.max(0, 1 - (now - this.callAt) / this.windowMs);
  }

  pause(now: number): void {
    if (this.status === 'playing' && this.pausedAt === null) this.pausedAt = now;
  }

  resume(now: number): void {
    if (this.pausedAt !== null) {
      const gap = now - this.pausedAt;
      this.pausedTotal += gap;
      this.callAt += gap; // the window picks up where it left off
      this.pausedAt = null;
    }
  }

  get paused(): boolean {
    return this.pausedAt !== null;
  }

  /** call every frame; returns an outcome when the window has closed */
  tick(now: number): Outcome | null {
    if (this.status !== 'playing' || this.pausedAt !== null || !this.call) return null;
    if (now - this.callAt < this.windowMs) return null;
    return this.resolve('timeout', now);
  }

  input(dir: Dir, now: number): Outcome | null {
    if (this.status !== 'playing' || this.pausedAt !== null || !this.call) return null;
    return this.resolve(dir, now);
  }

  private resolve(did: Dir | 'timeout', now: number): Outcome {
    const call = this.call!;
    const expected = call.code[this.cursor];
    const ok = did !== 'timeout' && did === expected;
    let landed = false;
    let lost = false;

    if (ok) {
      this.cursor++;
      this.streak++;
      this.bestStreak = Math.max(this.bestStreak, this.streak);
      if (this.cursor >= call.code.length) {
        landed = true;
        this.landed++;
      }
    } else {
      this.mistakes++;
      this.streak = 0;
      this.penalties += this.rules.penaltyMs;
      if (did === 'timeout') lost = true;
      // a wrong press costs time and the progress on this code, but not the call-in
      this.cursor = 0;
    }

    let won = false;
    if (landed || lost) {
      this.index++;
      this.cursor = 0;
      this.callAt = now;
      if (this.index >= this.queue.length) {
        this.status = 'won';
        won = true;
      }
    }
    return { ok, did, expected, call, landed, lost, won };
  }
}

/** a tiny seeded RNG (mulberry32) for tests and replays */
export function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// score formatting is shared; re-exported so this game's own modules keep importing from here
export { formatTime } from '../../ui/format.ts';
